'use client';

import { memo, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { MarketoFormElement } from './components/marketo-form-element';
import { MarketoStylesheets } from './components/marketo-stylesheets';
import { MarketoThankYou } from './components/marketo-thank-you';
import { useMarketoForm } from './hooks/use-marketo-form';
import './marketo-form.scss';
import type { MarketoFormProps } from './marketo-form.types';

export const MarketoForm = memo((props: MarketoFormProps) => {
  const { formContent, thankYouUrl } = props;
  const searchParams = useSearchParams();
  const [submitted, setSubmitted] = useState(false);

  // Success handler passed to the hook — called after Marketo AJAX submission
  const handleFormSuccess = useCallback(() => {
    if (thankYouUrl) {
      window.location.href = thankYouUrl;
    } else {
      setSubmitted(true);
    }
  }, [thankYouUrl]);

  const { formId, resetForm } = useMarketoForm({ ...props, onFormSuccess: handleFormSuccess });

  // Legacy: aliId in URL means Marketo did a full-page redirect (fallback)
  const aliId = searchParams.get('aliId');
  const stateParam = searchParams.get('state');
  const showThankYou = submitted || stateParam === 'thank-you';

  useEffect(() => {
    if (aliId) {
      if (thankYouUrl) {
        window.location.href = thankYouUrl;
        return;
      }

      const params = new URLSearchParams(searchParams.toString());
      if (params.get('state') !== 'thank-you') {
        params.set('state', 'thank-you');
      }
      params.delete('aliId');

      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [aliId, searchParams, thankYouUrl]);

  const handleGoBack = useCallback(() => {
    resetForm();
    setSubmitted(false);

    // Clean URL params without triggering navigation
    const params = new URLSearchParams(searchParams.toString());
    params.delete('aliId');
    params.delete('state');

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [searchParams, resetForm]);

  return (
    <>
      <MarketoStylesheets />
      {showThankYou && <MarketoThankYou onGoBack={handleGoBack} formContent={formContent} />}
      <div style={showThankYou ? { display: 'none' } : undefined}>
        <MarketoFormElement formId={formId} />
      </div>
    </>
  );
});

MarketoForm.displayName = 'MarketoForm';
